/**
 * Framework-free type-ahead suggestion scheduling.
 *
 * Owns debouncing, the LRU response cache and request cancellation so the
 * React hook and the embeddable widget share one implementation. The actual
 * HTTP call is injected, because the widget cannot use `apiService`.
 */

import type { SuggestionItem } from '../types/search';

export interface SuggestParams {
  q: string;
  size: number;
  user_language?: string;
  audience?: string;
}

export type SuggestFn = (
  params: SuggestParams,
  signal: AbortSignal
) => Promise<SuggestionItem[]>;

export const DEFAULT_MIN_LENGTH = 2;
export const DEFAULT_DEBOUNCE_MS = 120;
export const DEFAULT_SIZE = 8;

const CACHE_CAPACITY = 32;
const CACHE_TTL_MS = 30_000;

interface CacheEntry {
  value: SuggestionItem[];
  expiresAt: number;
}

// Module-level LRU shared across all consumers in the page.
const cache = new Map<string, CacheEntry>();

// Params are part of the key: a page may host several consumers requesting
// different sizes or languages for the same prefix.
function cacheKey(params: SuggestParams): string {
  return [
    params.q,
    params.size,
    params.user_language ?? '',
    params.audience ?? '',
  ].join('\u0000');
}

function cacheGet(key: string): SuggestionItem[] | null {
  const entry = cache.get(key);
  if (!entry) return null;
  if (entry.expiresAt < Date.now()) {
    cache.delete(key);
    return null;
  }
  // refresh LRU order
  cache.delete(key);
  cache.set(key, entry);
  return entry.value;
}

function cacheSet(key: string, value: SuggestionItem[]): void {
  if (cache.size >= CACHE_CAPACITY) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey !== undefined) cache.delete(oldestKey);
  }
  cache.set(key, { value, expiresAt: Date.now() + CACHE_TTL_MS });
}

export interface SuggestionRequestOptions {
  suggest: SuggestFn;
  minLength?: number;
  debounceMs?: number;
  size?: number;
  userLanguage?: string;
  audience?: string;
}

export interface SuggestionRequestHandlers {
  onLoading(isLoading: boolean): void;
  onData(suggestions: SuggestionItem[]): void;
  onError(error: Error): void;
}

/**
 * Schedule a suggestion fetch for `query`. Returns a cancel function that
 * clears the pending debounce timer and aborts any in-flight request.
 */
export function requestSuggestions(
  query: string,
  options: SuggestionRequestOptions,
  handlers: SuggestionRequestHandlers
): () => void {
  const {
    suggest,
    minLength = DEFAULT_MIN_LENGTH,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    size = DEFAULT_SIZE,
    userLanguage,
    audience,
  } = options;

  const trimmed = query.trim().toLowerCase();

  if (trimmed.length < minLength) {
    handlers.onData([]);
    handlers.onLoading(false);
    return () => {};
  }

  const params: SuggestParams = {
    q: trimmed,
    size,
    user_language: userLanguage,
    audience,
  };

  // Cache hit resolves synchronously, avoiding a loading-state flicker.
  const cached = cacheGet(cacheKey(params));
  if (cached) {
    handlers.onData(cached);
    handlers.onLoading(false);
    return () => {};
  }

  handlers.onLoading(true);

  let controller: AbortController | null = null;
  const timer = setTimeout(() => {
    controller = new AbortController();
    suggest(params, controller.signal)
      .then((suggestions) => {
        if (controller?.signal.aborted) return;
        cacheSet(cacheKey(params), suggestions);
        handlers.onData(suggestions);
        handlers.onLoading(false);
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') {
          // superseded by a newer request — stay quiet
          return;
        }
        handlers.onError(err instanceof Error ? err : new Error(String(err)));
        handlers.onData([]);
        handlers.onLoading(false);
      });
  }, debounceMs);

  return () => {
    clearTimeout(timer);
    controller?.abort();
  };
}
